import { FlexBox } from '@airship-platform/jam-web/components';
import { useAppointmentProposalOptions } from '../../../../data-access/appointmentProposalOptions';
import {
  type AggregatedOptionActivity,
  aggregateOptionActivity,
  metricLabel,
  useProposalEngagement,
} from '../../../../data-access/proposalEngagement';
import BreakdownCard from '../engagement-section/BreakdownCard';
import EngagementBanner from '../engagement-section/EngagementBanner';
import EngagementCard from '../engagement-section/EngagementCard';

interface OwnProps {
  appointmentProposalId: string;
}

const ProposalEngagementSummary: React.FC<OwnProps> = ({
  appointmentProposalId,
}) => {
  const { options } = useAppointmentProposalOptions();
  const { proposalEngagements, loadingProposalIds } = useProposalEngagement();

  const isLoading = loadingProposalIds.includes(appointmentProposalId);
  const engagement = proposalEngagements.find(
    (e) => e.proposalId === appointmentProposalId
  );

  const optionName = (optionGuid: string | null) =>
    options.find((option) => option.appointmentProposalOptionId === optionGuid)
      ?.name;

  const activityBreakdown = engagement?.activityBreakdown || [];
  const aggregatedOptions = aggregateOptionActivity(activityBreakdown);
  const contentRows = activityBreakdown.filter(
    (activity) => !activity.optionGuid
  );

  const standout = aggregatedOptions.reduce<
    AggregatedOptionActivity | undefined
  >(
    (top, option) =>
      !top || option.totalMinutesSpent > top.totalMinutesSpent ? option : top,
    undefined
  );

  const optionItems = aggregatedOptions.map((option) => ({
    label: optionName(option.optionGuid) ?? 'Unnamed option',
    metric: metricLabel(option),
  }));
  const contentItems = contentRows.map((activity) => ({
    label: activity.sectionName,
    metric: metricLabel(activity),
  }));

  return (
    <FlexBox flexDirection={'column'} gap={2} width={'100%'} mb={3}>
      <EngagementCard isLoading={isLoading} engagement={engagement}>
        <EngagementBanner
          isLoading={isLoading}
          engagement={engagement}
          standout={standout}
          optionName={optionName}
        />
      </EngagementCard>

      <FlexBox flexDirection={['column', 'row']} gap={2} width={'100%'}>
        <BreakdownCard
          isLoading={isLoading}
          title={'Option breakdown'}
          items={optionItems}
        />
        <BreakdownCard
          isLoading={isLoading}
          title={'Content breakdown'}
          items={contentItems}
        />
      </FlexBox>
    </FlexBox>
  );
};

export default ProposalEngagementSummary;
